import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

const MeetingPresentation = () => {
  const navigate = useNavigate();
  const { clientId, meetingId } = useParams();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [visibleItems, setVisibleItems] = useState([]);
  
  // State for editable fields (Lopes onboarding)
  const [funnelData, setFunnelData] = useState(() => {
    const saved = localStorage.getItem(`funnel_${clientId}_${meetingId}`);
    return saved ? JSON.parse(saved) : {
      custoLead: '15,00',
      custoAgendamento: '150,00',
      custoVisita: '300,00',
      custoVenda: '1500,00'
    };
  });

  // State for Paradise funnel (budget-based)
  const [paradiseFunnelData, setParadiseFunnelData] = useState(() => {
    const saved = localStorage.getItem(`paradise_funnel_${clientId}_${meetingId}`);
    return saved ? JSON.parse(saved) : {
      verba: clientId === 'paradise' ? '5000' : '0',
      custoLead: clientId === 'paradise' ? '15' : '0',
      taxaAgendamento: clientId === 'paradise' ? '10' : '0',
      taxaVisita: clientId === 'paradise' ? '50' : '0',
      taxaVenda: clientId === 'paradise' ? '20' : '0'
    };
  });

  // Calculate Paradise funnel metrics
  const calculateParadiseFunnel = () => {
    const verba = parseFloat(paradiseFunnelData.verba.replace(',', '.')) || 0;
    const custoLead = parseFloat(paradiseFunnelData.custoLead.replace(',', '.')) || 0;
    const taxaAgendamento = parseFloat(paradiseFunnelData.taxaAgendamento.replace(',', '.')) || 0;
    const taxaVisita = parseFloat(paradiseFunnelData.taxaVisita.replace(',', '.')) || 0;
    const taxaVenda = parseFloat(paradiseFunnelData.taxaVenda.replace(',', '.')) || 0;

    const totalLeads = custoLead > 0 ? verba / custoLead : 0;
    const custoAgendamento = taxaAgendamento > 0 ? custoLead / (taxaAgendamento / 100) : 0;
    const custoVisita = taxaVisita > 0 ? custoAgendamento / (taxaVisita / 100) : 0;
    const custoVenda = taxaVenda > 0 ? custoVisita / (taxaVenda / 100) : 0;

    return {
      totalLeads: totalLeads.toFixed(0),
      custoAgendamento: custoAgendamento.toFixed(2),
      custoVisita: custoVisita.toFixed(2),
      custoVenda: custoVenda.toFixed(2)
    };
  };
  const [selectedTime, setSelectedTime] = useState(() => {
    const saved = localStorage.getItem(`schedule_${clientId}_${meetingId}`);
    if (saved) return saved;
    // Default to 16h for Paradise, 12h for others
    return clientId === 'paradise' ? '16h' : '12h';
  });
  const [copied, setCopied] = useState(false);
  
  // Default notes - empty for Paradise, filled for others
  const defaultNotes = clientId === 'paradise' ? {
    empreendimentos: 'Paradiso Wellness Living',
    conexoesCRM: 'VMV - Passar dados do funil'
  } : {
    empreendimentos: `• Green Park
• Lissoni
• Grand Quartier
• Zona Sul`,
    conexoesCRM: `Funcionalidades Essenciais:
• Automação de fluxo de vendas
• Gestão de tarefas e agendamento
• Visualização Kanban
• Visualização em lista
• Telefonia integrada?
• Integração com WhatsApp
• Assistente de IA integrado
• Integrações com mídias sociais
• Identificação da origem do lead (rastreamento por conjunto de anúncios)
• Relatórios completos (BI + Análise de Dados)

Tudo funcionando dentro de um único CRM.`
  };
  
  const [notes, setNotes] = useState(() => {
    // For Paradise, always start with empty fields
    if (clientId === 'paradise') {
      const saved = localStorage.getItem(`notes_${clientId}_${meetingId}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // Only use saved data if it's actually from user input (not default values)
          return {
            empreendimentos: parsed.empreendimentos || '',
            conexoesCRM: parsed.conexoesCRM || ''
          };
        } catch {
          return { empreendimentos: '', conexoesCRM: '' };
        }
      }
      return { empreendimentos: '', conexoesCRM: '' };
    }
    
    // For other clients, use saved or default values
    const saved = localStorage.getItem(`notes_${clientId}_${meetingId}`);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        empreendimentos: parsed.empreendimentos || defaultNotes.empreendimentos,
        conexoesCRM: parsed.conexoesCRM || defaultNotes.conexoesCRM
      };
    }
    return defaultNotes;
  });

  // Clear old data for Paradise on mount
  useEffect(() => {
    if (clientId === 'paradise') {
      const saved = localStorage.getItem(`notes_${clientId}_${meetingId}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // Check if it contains old default values from other clients and clear them
          if (parsed.empreendimentos?.includes('Green Park') || 
              parsed.conexoesCRM?.includes('Funcionalidades Essenciais')) {
            localStorage.removeItem(`notes_${clientId}_${meetingId}`);
            setNotes({ 
              empreendimentos: 'Paradiso Wellness Living', 
              conexoesCRM: 'VMV - Passar dados do funil' 
            });
          }
        } catch (e) {
          // If parsing fails, clear it
          localStorage.removeItem(`notes_${clientId}_${meetingId}`);
        }
      }
    }
  }, [clientId, meetingId]);

  // Auto-save to localStorage
  useEffect(() => {
    localStorage.setItem(`funnel_${clientId}_${meetingId}`, JSON.stringify(funnelData));
  }, [funnelData, clientId, meetingId]);

  useEffect(() => {
    localStorage.setItem(`paradise_funnel_${clientId}_${meetingId}`, JSON.stringify(paradiseFunnelData));
  }, [paradiseFunnelData, clientId, meetingId]);

  useEffect(() => {
    localStorage.setItem(`schedule_${clientId}_${meetingId}`, selectedTime);
  }, [selectedTime, clientId, meetingId]);

  useEffect(() => {
    localStorage.setItem(`notes_${clientId}_${meetingId}`, JSON.stringify(notes));
  }, [notes, clientId, meetingId]);

  const meetingData = {
    'camargo': {
      '13-11-2025': {
        date: '13/11/2025',
        client: 'Camargo',
        title: 'Check-in Imobiliária Camargo'
      },
      '20-11-2025': {
        date: '20/11/2025',
        client: 'Camargo',
        title: 'Check-in Imobiliária Camargo'
      },
      '27-11-2025': {
        date: '27/11/2025',
        client: 'Camargo',
        title: 'Check-in Imobiliária Camargo'
      },
      '03-12-2025': {
        date: '03/12/2025',
        client: 'Camargo',
        title: 'Check-in Imobiliária Camargo'
      },
      '12-12-2025': {
        date: '12/12/2025',
        client: 'Camargo',
        title: 'Check-in Imobiliária Camargo'
      },
      '26-12-2025': {
        date: '26/12/2025',
        client: 'Camargo',
        title: 'Check-in Imobiliária Camargo'
      }
    },
    'apolar': {
      '28-11-2025': {
        date: '28/11/2025',
        client: 'Apolar',
        title: 'Check-in Apolar'
      },
      '05-12-2025': {
        date: '05/12/2025',
        client: 'Apolar',
        title: 'Check-in Apolar'
      }
    },
    'lopes': {
      'onboarding': {
        date: 'Onboarding',
        client: 'Lopes',
        title: 'Reunião de Onboarding'
      }
    },
    'paradise': {
      'start': {
        date: 'Start',
        client: 'Paradise',
        title: 'Reunião de Start'
      },
      'onboarding': {
        date: 'Onboarding',
        client: 'Paradise',
        title: 'Reunião de Onboarding'
      }
    }
  };

  const meeting = meetingData[clientId]?.[meetingId];

  const slidesData = {
    'camargo': {
      '13-11-2025': [
        {
          type: 'cover',
          title: 'Check-in Imobiliária Camargo',
          subtitle: '13/11 - Novas Conversas, Aba de Documentação e Resultado do teste A/B'
        },
        {
          type: 'section',
          title: 'Melhorias na Plataforma',
          items: [
            'Mudança completa na visualização das conversas',
            'Ajustes no kanban para melhor uso (correção de atualização de página)',
            'Adição da parte documentação dentro da própria plataforma',
            'Ajustes em Fuga do Script',
            'Bug do Horário indisponível',
            'Sugestões de horários errados na ligação',
            'Filtro para responsável adicionado',
            'Criado a opção de responsável: Tebaldi',
            'Usuário padrão na importação alterado',
            'Lead marcando como qualificado sem interação'
          ]
        },
        {
          type: 'needs',
          title: 'O que preciso',
          items: [
            'Script de cadência de follow up em texto para criar de forma manual'
          ]
        },
        {
          type: 'divider',
          title: 'Campanha',
          subtitle: 'Análises da Semana'
        },
        {
          type: 'intro',
          title: 'Teste A/B',
          content: 'Rodamos a mesma verba em 2 anúncios diferentes:',
          items: [
            'AD 1: Ad campeão com adaptação para avisar que a Bianca ligará',
            'AD 2: Nosso Ad das fachadas com os textos adicionados'
          ]
        },
        {
          type: 'comparison',
          title: 'Resultados',
          ad1: {
            name: 'AD 1 - Campeão Adaptado',
            metrics: [
              { label: 'CPL', value: 'R$ 8,52' },
              { label: 'Taxa de agendamento', value: '4%' },
              { label: 'Custo por Agendamento', value: 'R$ 191,70' }
            ]
          },
          ad2: {
            name: 'AD 2 - Fachadas',
            metrics: [
              { label: 'CPL', value: 'R$ 13,81', highlight: '61% mais caro' },
              { label: 'Taxa de agendamento', value: '19%', highlight: '5x mais agendamentos (400%)' },
              { label: 'Custo por Agendamento', value: 'R$ 72,82', highlight: '63% mais barato (2,63x)' }
            ],
            note: 'Obs: Por conta da pausa na campanha essa taxa piorou, no global ela é 26% neste anúncio'
          }
        },
        {
          type: 'suggestion',
          title: 'Sugestão',
          content: 'Deixar rodar somente o nosso ad com verba total',
          reason: 'Se mantivermos o custo por agendamento usando verba total, vai faltar corretor para atender'
        },
        {
          type: 'summary',
          title: 'Ata da Reunião',
          date: '13/11/2025',
          sections: [
            {
              title: 'Melhorias Implementadas',
              items: [
                'Nova visualização de conversas',
                'Correções no kanban',
                'Aba de documentação integrada',
                'Correções de bugs (horário, script, qualificação)',
                'Novos filtros e opções de responsável'
              ]
            },
            {
              title: 'Pendências',
              items: [
                'Script de cadência de follow up em texto'
              ]
            },
            {
              title: 'Campanha - Decisões',
              items: [
                'AD 2 (Fachadas) apresentou melhor performance',
                'Custo por agendamento 63% mais barato',
                'Taxa de agendamento 5x superior',
                'Decisão: Concentrar verba total no AD 2'
              ]
            }
          ]
        }
      ],
      '20-11-2025': [
        {
          type: 'cover',
          title: 'Check-in Imobiliária Camargo',
          subtitle: '20/11 - Atualização em documentos, Atualização no FUP, Nova VAPI, Resultado dos testes A/B'
        },
        {
          type: 'section',
          title: 'Melhorias na Plataforma',
          items: [
            'Atualizações em documentos:',
            'Adição de formulário para contrato',
            'Adição de contrato gerado dinamicamente',
            'Adição da lista de documentos necessários',
            'Adição de geração de Processo com junção de PDFs',
            'Adição de lead fora da Bianca',
            'FUP adicionado na marra',
            'Criação de um MVP de Clone da Vapi'
          ]
        },
        {
          type: 'divider',
          title: 'Campanha',
          subtitle: 'Análises da Semana'
        },
        {
          type: 'intro',
          title: 'Teste A/B',
          content: 'Rodamos a mesma verba em 3 anúncios diferentes:',
          items: [
            'AD 1: Ad campeão com adaptação para avisar que a Bianca ligará',
            'AD 2: Nosso Ad das fachadas com os textos adicionados',
            'AD 3: Novo teste com ads campeões intercalados'
          ]
        },
        {
          type: 'comparison',
          title: 'Resultados',
          ad1: {
            name: 'AD 1 - Campeão Adaptado',
            metrics: [
              { label: 'CPL', value: 'R$ 8,52' },
              { label: 'Taxa de agendamento', value: '4%' },
              { label: 'Custo por Agendamento', value: 'R$ 191,70' }
            ]
          },
          ad2: {
            name: 'AD 2 - Fachadas',
            metrics: [
              { label: 'CPL', value: 'R$ 13,81' },
              { label: 'Taxa de agendamento', value: '19%' },
              { label: 'Custo por Agendamento', value: 'R$ 72,82' }
            ]
          },
          ad3: {
            name: 'AD 3 - Campeões Intercalados',
            metrics: [
              { label: 'CPL', value: 'R$ 13,57' },
              { label: 'Taxa de agendamento', value: '18%' },
              { label: 'Custo por Agendamento', value: 'R$ 75,43' }
            ]
          }
        },
        {
          type: 'suggestion',
          title: 'Sugestão',
          content: 'Rodar mais uma semana pois estão muito próximos',
          reason: 'Um pouco mais de dados ajudaria a tomar essa decisão melhor'
        },
        {
          type: 'summary',
          title: 'Ata da Reunião',
          date: '20/11/2025',
          sections: [
            {
              title: 'Melhorias Implementadas - Documentos',
              items: [
                'Adição de formulário para contrato',
                'Adição de contrato gerado dinamicamente',
                'Adição da lista de documentos necessários',
                'Adição de geração de Processo com junção de PDFs',
                'Adição de lead fora da Bianca'
              ]
            },
            {
              title: 'Melhorias Implementadas - Sistema',
              items: [
                'FUP adicionado na marra',
                'Criação de um MVP de Clone da Vapi'
              ]
            },
            {
              title: 'Campanha - Teste A/B (3 anúncios)',
              items: [
                'AD 1 (Campeão Adaptado): CPL R$ 8,52 | Taxa 4% | CPA R$ 191,70',
                'AD 2 (Fachadas): CPL R$ 13,81 | Taxa 19% | CPA R$ 72,82',
                'AD 3 (Campeões Intercalados): CPL R$ 13,57 | Taxa 18% | CPA R$ 75,43',
                'Decisão: Rodar mais uma semana - resultados muito próximos',
                'Mais dados necessários para decisão definitiva'
              ]
            }
          ]
        }
      ],
      '27-11-2025': [
        {
          type: 'cover',
          title: 'Check-in Imobiliária Camargo',
          subtitle: '27/11 - Atualização no Dashboard, Ponte SDR, Correções de bugs, Atualizações em Calendario, Resultado do teste A/B'
        },
        {
          type: 'section',
          title: 'Melhorias Publicadas',
          items: [
            '\\n sendo enviados no FUP corrigido',
            'Removido o FUP1, começando direto pelo 2',
            'Corrigido o erro de qualificação e por consequência a fuga do script',
            'Melhorada a lógica de envio de informações pro WhatsApp',
            'Corrigido o bug que impedia 2 agendamentos no mesmo horário',
            'Ocultado dados do contrato',
            'Botão de documento não necessário adicionado',
            'Separação de documentos do processo',
            'Separar termo de horários e termo de bônus em dois campos',
            'Opção de remover o arquivo upado',
            'Folha de pagamento aceita múltiplos arquivos',
            'IR aceita múltiplos arquivos',
            'Múltiplos arquivos: permitir renomear individualmente cada um',
            'Certidões de nascimento de dependentes: botão "não precisa" adicionado'
          ]
        },
        {
          type: 'needs',
          title: 'Em Finalização',
          items: [
            { text: 'Conexão com Google Drive', icon: '☁️' },
            { text: 'Atualização no Dashboard', icon: '📊' },
            { text: 'Ponte SDR', icon: '🔗' },
            { text: 'Atualizações em Calendário', icon: '📅' }
          ]
        },
        {
          type: 'divider',
          title: 'Campanha',
          subtitle: 'Análises da Semana'
        },
        {
          type: 'intro',
          title: 'Teste A/B',
          content: 'Rodamos a mesma verba em 3 anúncios diferentes:',
          items: [
            'AD 1: Ad campeão com adaptação para avisar que a Bianca ligará',
            'AD 2: Nosso Ad das fachadas com os textos adicionados',
            'AD 3: Novo teste com ads campeões intercalados'
          ]
        },
        {
          type: 'comparison',
          title: 'Resultados',
          ad1: {
            name: 'AD 1 - Campeão Adaptado',
            metrics: [
              { label: 'CPL', value: 'R$ 8,52' },
              { label: 'Taxa de agendamento', value: '4%' },
              { label: 'Custo por Agendamento', value: 'R$ 191,70' }
            ]
          },
          ad2: {
            name: 'AD 2 - Fachadas',
            metrics: [
              { label: 'CPL', value: 'R$ 15,13' },
              { label: 'Taxa de agendamento', value: '19%' },
              { label: 'Custo por Agendamento', value: 'R$ 80,04' }
            ]
          },
          ad3: {
            name: 'AD 3 - Campeões Intercalados',
            metrics: [
              { label: 'CPL', value: 'R$ 10,27', highlight: '32% mais barato que AD 2' },
              { label: 'Taxa de agendamento', value: '19%', highlight: 'Mesma taxa' },
              { label: 'Custo por Agendamento', value: 'R$ 53,13', highlight: '34% mais barato que AD 2' }
            ],
            note: 'Obs: CPL nos últimos 7 dias está em R$ 8,69 e caindo'
          }
        },
        {
          type: 'suggestion',
          title: 'Sugestão',
          content: 'Aumentar a verba do AD 03 e diminuir o AD 2',
          reason: 'AD 2 deve rodar em verba baixa por uma semana, com tendência de ser pausado. AD 3 está apresentando melhor custo por agendamento.'
        },
        {
          type: 'summary',
          title: 'Ata da Reunião',
          date: '27/11/2025',
          sections: [
            {
              title: 'Melhorias Publicadas',
              items: [
                '\\n sendo enviados no FUP corrigido',
                'Removido o FUP1, começando direto pelo 2',
                'Corrigido o erro de qualificação e fuga do script',
                'Melhorada lógica de envio pro WhatsApp',
                'Corrigido bug de 2 agendamentos no mesmo horário',
                'Ocultado dados do contrato',
                'Botão de documento não necessário',
                'Separação de documentos do processo',
                'Termo de horários e bônus separados',
                'Opção de remover arquivo upado',
                'Folha de pagamento e IR aceitam múltiplos arquivos',
                'Renomear arquivos individualmente',
                'Certidões de dependentes com botão "não precisa"'
              ]
            },
            {
              title: 'Em Finalização',
              items: [
                'Conexão com Google Drive',
                'Atualização no Dashboard',
                'Ponte SDR',
                'Atualizações em Calendário'
              ]
            },
            {
              title: 'Campanha - Teste A/B (3 anúncios)',
              items: [
                'AD 1 (Campeão Adaptado): CPL R$ 8,52 | Taxa 4% | CPA R$ 191,70',
                'AD 2 (Fachadas): CPL R$ 15,13 | Taxa 19% | CPA R$ 80,04',
                'AD 3 (Campeões Intercalados): CPL R$ 10,27 | Taxa 19% | CPA R$ 53,13 ⭐',
                'CPL últimos 7 dias: R$ 8,69 e caindo',
                'Decisão: Aumentar verba AD 3, diminuir AD 2 (tendência de pausa)'
              ]
            }
          ]
        }
      ],
      '03-12-2025': [
        {
          type: 'cover',
          title: 'Check-in Imobiliária Camargo',
          subtitle: '03/12 - Novo Dashboard, Ponte SDR, Resultado dos Anúncios'
        },
        {
          type: 'section',
          title: 'Melhorias Publicadas',
          items: [
            'Implementação da Ponte SDR',
            'Novo Dashboard',
            'Ajustes em documentos'
          ]
        },
        {
          type: 'divider',
          title: 'Campanha',
          subtitle: 'Análises da Semana'
        },
        {
          type: 'intro',
          title: 'Teste A/B',
          content: 'Rodamos a mesma verba em 2 anúncios diferentes:',
          items: [
            'AD 2: Nosso Ad das fachadas com os textos adicionados',
            'AD 3: Novo teste com ads campeões intercalados'
          ]
        },
        {
          type: 'comparison',
          title: 'Resultados',
          ad1: {
            name: 'AD 2 - Fachadas',
            metrics: [
              { label: 'CPL', value: 'R$ 20,22' },
              { label: 'Taxa de agendamento', value: '14%', highlight: '2x mais conversão' },
              { label: 'Custo por Agendamento', value: 'R$ 144,89' }
            ]
          },
          ad2: {
            name: 'AD 3 - Campeões Intercalados',
            metrics: [
              { label: 'CPL', value: 'R$ 10,84', highlight: '46% mais barato' },
              { label: 'Taxa de agendamento', value: '7%' },
              { label: 'Custo por Agendamento', value: 'R$ 149,94' }
            ]
          }
        },
        {
          type: 'suggestion',
          title: 'Sugestão',
          content: 'Voltar para a verba da semana passada'
        },
        {
          type: 'summary',
          title: 'Ata da Reunião',
          date: '03/12/2025',
          sections: [
            {
              title: 'Melhorias Publicadas',
              items: [
                'Implementação da Ponte SDR - sistema de conexão entre SDR e Lead no pós agendamento',
                'Novo Dashboard com métricas atualizadas e visualização otimizada',
                'Ajustes em documentos'
              ]
            },
            {
              title: 'Campanha - Teste A/B (2 anúncios)',
              items: [
                'AD 2 (Fachadas): CPL R$ 20,22 | Taxa 14% | CPA R$ 144,89',
                'AD 3 (Campeões Intercalados): CPL R$ 10,84 | Taxa 7% | CPA R$ 149,94',
                'AD 3 tem CPL 46% mais barato, porém AD 2 converte 2x mais',
                'CPAs muito próximos entre os dois anúncios',
                'Decisão: Voltar para a verba da semana passada'
              ]
            }
          ]
        }
      ],
      '12-12-2025': [
        {
          type: 'cover',
          title: 'Check-in Imobiliária Camargo',
          subtitle: '12/12 - Proposta, Contrato, Resultado dos Anúncios'
        },
        {
          type: 'section',
          title: 'Melhorias Publicadas',
          items: [
            'Proposta',
            'Conexão com drive'
          ]
        },
        {
          type: 'divider',
          title: 'Campanha',
          subtitle: 'Análises da Semana'
        },
        {
          type: 'intro',
          title: 'Resultados da Campanha',
          content: 'Rodamos a mesma verba em 2 anúncios diferentes:',
          items: [
            'AD 2: Nosso Ad das fachadas com os textos adicionados',
            'AD 3: Novo teste com ads campeões intercalados'
          ]
        },
        {
          type: 'comparison',
          title: 'Resultados',
          ad1: {
            name: 'AD 2 - Fachadas',
            metrics: [
              { label: 'CPL', value: 'R$ 15,26' },
              { label: 'Taxa de agendamento', value: '13.3%' },
              { label: 'Custo por Agendamento', value: 'R$ 114,43' }
            ]
          },
          ad2: {
            name: 'AD 3 - Campeões Intercalados',
            metrics: [
              { label: 'CPL', value: 'R$ 12,70' },
              { label: 'Taxa de agendamento', value: '11.1%' },
              { label: 'Custo por Agendamento', value: 'R$ 114,29' }
            ]
          }
        },
        {
          type: 'suggestion',
          title: 'Sugestão',
          content: 'Alterar a verba para o valor de 3 semanas atrás, e esperar pelo menos duas semanas para a análise'
        },
        {
          type: 'summary',
          title: 'Ata da Reunião',
          date: '12/12/2025',
          sections: [
            {
              title: 'Melhorias Publicadas',
              items: [
                'Proposta',
                'Conexão com drive'
              ]
            },
            {
              title: 'Campanha - Resultados (2 anúncios)',
              items: [
                'AD 2 (Fachadas): CPL R$ 15,26 | Taxa 13.3% | CPA R$ 114,43',
                'AD 3 (Campeões Intercalados): CPL R$ 12,70 | Taxa 11.1% | CPA R$ 114,29',
                'CPAs muito próximos entre os dois anúncios',
                'Decisão: Alterar verba para valor de 3 semanas atrás e aguardar 2 semanas para análise'
              ]
            }
          ]
        }
      ],
      '26-12-2025': [
        {
          type: 'cover',
          title: 'Check-in Imobiliária Camargo',
          subtitle: '26/12 - Google Calendar, Proposta em Produção, Nova BM, Resultado dos Anúncios'
        },
        {
          type: 'section',
          title: 'Melhorias Publicadas',
          items: [
            'Google Calendar',
            'Ajustes para colocar proposta em produção',
            'Adicionado nova BM'
          ]
        },
        {
          type: 'divider',
          title: 'Campanha',
          subtitle: 'Análises da Semana'
        },
        {
          type: 'intro',
          title: 'Resultados da Campanha',
          content: 'Rodamos a mesma verba em 2 anúncios diferentes:',
          items: [
            'AD 1: Ad campeão com adaptação para avisar que a Bianca ligará',
            'AD 2: Nosso Ad das fachadas com os textos adicionados'
          ]
        },
        {
          type: 'comparison',
          title: 'Resultados',
          ad1: {
            name: 'AD 1 - Campeão Adaptado',
            metrics: [
              { label: 'CPL', value: 'R$ 26,73' },
              { label: 'Taxa de agendamento', value: '7,7%', highlight: '28% maior conversão' },
              { label: 'Custo por Agendamento', value: 'R$ 361,54' }
            ]
          },
          ad2: {
            name: 'AD 2 - Fachadas',
            metrics: [
              { label: 'CPL', value: 'R$ 16,50', highlight: '38% mais barato' },
              { label: 'Taxa de agendamento', value: '6%' },
              { label: 'Custo por Agendamento', value: 'R$ 275,21', highlight: '24% mais barato' }
            ]
          }
        },
        {
          type: 'analysis',
          title: 'Análise Profunda',
          items: [
            { metric: 'CPL', comparison: 'AD 2 tem CPL 38% mais barato (R$ 16,50 vs R$ 26,73)', winner: 'AD 2' },
            { metric: 'Taxa de Conversão', comparison: 'AD 1 converte 28% mais leads em agendamentos (7,7% vs 6%)', winner: 'AD 1' },
            { metric: 'CPA', comparison: 'AD 2 tem custo por agendamento 24% menor (R$ 275,21 vs R$ 361,54)', winner: 'AD 2' }
          ],
          conclusion: 'Apesar do AD 1 ter melhor taxa de conversão, o AD 2 apresenta melhor eficiência de custo no geral, com CPL e CPA significativamente menores.'
        },
        {
          type: 'suggestion',
          title: 'Sugestão',
          content: 'Não recomendo grandes mudanças neste momento',
          reason: 'Estamos no período de Natal e o leilão dispara nesta época. Sugiro deixar passar pelo menos mais uma semana antes de fazer mudanças, já que estamos na mesma configuração da semana campeã.'
        },
        {
          type: 'summary',
          title: 'Ata da Reunião',
          date: '26/12/2025',
          sections: [
            {
              title: 'Melhorias Publicadas',
              items: [
                'Integração com Google Calendar implementada',
                'Ajustes para colocar proposta em produção',
                'Adicionado nova Business Manager (BM)'
              ]
            },
            {
              title: 'Campanha - Resultados (2 anúncios)',
              items: [
                'AD 1 (Campeão Adaptado): CPL R$ 26,73 | Taxa 7,7% | CPA R$ 361,54',
                'AD 2 (Fachadas): CPL R$ 16,50 | Taxa 6% | CPA R$ 275,21',
                'AD 2 apresenta CPL 38% mais barato e CPA 24% menor',
                'AD 1 tem taxa de conversão 28% maior'
              ]
            },
            {
              title: 'Decisão',
              items: [
                'Manter configuração atual por pelo menos mais uma semana',
                'Período de Natal causa aumento no leilão de anúncios',
                'Configuração atual é a mesma da semana campeã',
                'Aguardar estabilização pós-feriado para tomar decisões'
              ]
            }
          ]
        }
      ]
    },
    'apolar': {
      '28-11-2025': [
        {
          type: 'cover',
          title: 'Check-in Apolar',
          subtitle: '28/11 - Correção Captcha, Ponte SDR, Sales Force'
        },
        {
          type: 'section',
          title: 'Introdução',
          items: [
            'Correção do bug do captcha',
            'Ponte SDR',
            'Adição de campo de agendamento no Sales Force'
          ]
        },
        {
          type: 'section',
          title: 'Melhorias no Sistema',
          items: [
            'Correção de bugs na extração de lista - tudo pronto para iniciar a extração. Link enviado para o Marcelo fazer o pagamento dos créditos',
            'Adicionado o campo de data do agendamento no Sales Force',
            'Criação de Sistema de conexão entre SDR e o Lead no pós agendamento (finalizando o desenvolvimento)'
          ]
        },
        {
          type: 'summary',
          title: 'Ata da Reunião',
          date: '28/11/2025',
          sections: [
            {
              title: 'Introdução',
              items: [
                'Correção do bug do captcha',
                'Ponte SDR',
                'Adição de campo de agendamento no Sales Force'
              ]
            },
            {
              title: 'Melhorias no Sistema',
              items: [
                'Correção de bugs na extração de lista - tudo pronto para iniciar a extração. Link enviado para o Marcelo fazer o pagamento dos créditos',
                'Adicionado o campo de data do agendamento no Sales Force',
                'Criação de Sistema de conexão entre SDR e o Lead no pós agendamento (finalizando o desenvolvimento)'
              ]
            }
          ]
        }
      ],
      '05-12-2025': [
        {
          type: 'cover',
          title: 'Check-in Apolar',
          subtitle: '05/12 - Avanço na Ponte SDR, Lista de Imobiliárias'
        },
        {
          type: 'section',
          title: 'Avanços da Semana',
          items: [
            'Avanço na Ponte SDR',
            'Lista de imobiliárias sendo rodada'
          ]
        },
        {
          type: 'summary',
          title: 'Ata da Reunião',
          date: '05/12/2025',
          sections: [
            {
              title: 'Avanços da Semana',
              items: [
                'Avanço na Ponte SDR',
                'Lista de imobiliárias sendo rodada'
              ]
            }
          ]
        }
      ]
    },
    'lopes': {
      'onboarding': [
        {
          type: 'onboarding-cover',
          title: 'Sejam Bem-Vindos, Lopes!',
          objectives: [
            'Alinhamento estratégico e definição de Metas',
            'Seleção de empreendimentos',
            'Definir necessidades específicas de conexão e CRM',
            'Agendar reunião de passagem de acessos (segunda-feira)'
          ]
        },
        {
          type: 'funnel',
          title: 'Alinhamento Estratégico e Definição de Metas',
          intro: 'Trabalhamos direcionados a objetivos. Por isso, gostamos de começar definindo as metas dentro do funil de aquisição.'
        },
        {
          type: 'empreendimentos',
          title: 'Seleção de Empreendimentos',
          intro: 'Iniciamos o desenvolvimento de um anúncio com o empreendimento enviado. Vamos mostrar o que temos até então e gostaríamos de definir quais empreendimentos serão anunciados.'
        },
        {
          type: 'crm',
          title: 'Definir Necessidades Específicas de Conexão e CRM',
          question: 'Quais plataformas precisamos conectar e, após conectadas, o que vocês gostariam que fosse feito?'
        },
        {
          type: 'schedule',
          title: 'Agendar Reunião de Passagem de Acessos',
          day: 'Segunda-feira',
          times: ['9h', '12h']
        },
        {
          type: 'onboarding-summary',
          title: 'Ata do Onboarding',
          client: 'Lopes'
        }
      ]
    },
    'paradise': {
      'start': [
        {
          type: 'cover',
          title: 'Reunião de Start',
          subtitle: 'Paradiso Wellness Club — Resultado da Implementação'
        },
        {
          type: 'section',
          title: 'O que Fizemos na Implementação',
          items: [
            'Desenvolvemos o Novo Funil da Paradiso Wellness Club',
            'Novo Anúncio feito com IA',
            'Nova Plataforma para criação de mais anúncios',
            'Analisamos as campanhas antigas do Google e Meta',
            'Deixamos criado no rascunho nossa campanha de Lead Forms com a Api de conversões configurada',
            'Criamos o Script da Bianca com objetivo de agendamento de reunião',
            'Conectamos dashboard para puxar todos os dados da campanha',
            'Configuramos a plataforma'
          ]
        },
        {
          type: 'needs',
          title: 'O que Falta',
          items: [
            { text: 'Temos um documento com todo o processo de migração da Bianca', icon: '📄' },
            { text: 'Peço que revisem o documento para definirmos priorização de tarefas', icon: '📋' },
            { text: 'Objetivo: deixar o sistema do jeito que vocês esperam', icon: '🎯' }
          ]
        },
        {
          type: 'crm',
          title: 'Acessos à Plataforma',
          question: 'Quais os emails e telefones que devo dar acesso à plataforma?'
        },
        {
          type: 'intro',
          title: 'Próximos Passos',
          content: 'Para darmos continuidade ao projeto:',
          items: [
            'Ativar a campanha',
            'Marcar check-in semanal: sugestão quarta 17h'
          ]
        },
        {
          type: 'summary',
          title: 'Ata da Reunião de Start',
          date: new Date().toLocaleDateString('pt-BR'),
          sections: [
            {
              title: 'Implementação Realizada',
              items: [
                'Novo Funil da Paradiso Wellness Club desenvolvido',
                'Novo Anúncio feito com IA',
                'Nova Plataforma para criação de anúncios',
                'Análise das campanhas antigas (Google e Meta)',
                'Campanha de Lead Forms com Api de conversões configurada (rascunho)',
                'Script da Bianca criado para agendamento de reunião',
                'Dashboard conectado para dados da campanha',
                'Plataforma configurada'
              ]
            },
            {
              title: 'Pendências',
              items: [
                'Revisão do documento de migração da Bianca',
                'Priorização de tarefas a serem desenvolvidas'
              ]
            },
            {
              title: 'Próximos Passos',
              items: [
                'Ativar a campanha',
                'Check-in semanal: Quarta-feira às 17h'
              ]
            }
          ]
        }
      ],
      'onboarding': [
        {
          type: 'onboarding-cover',
          title: 'Sejam Bem-Vindos, Paradise!',
          objectives: [
            'Alinhamento estratégico e definição de Metas',
            'Seleção de empreendimentos',
            'Definir necessidades específicas de conexão e CRM',
            'Agendar reunião de passagem de acessos (segunda-feira)'
          ]
        },
        {
          type: 'paradise-funnel',
          title: 'Alinhamento Estratégico e Definição de Metas',
          intro: 'Trabalhamos direcionados a objetivos. Por isso, gostamos de começar definindo as metas dentro do funil de aquisição.'
        },
        {
          type: 'empreendimentos',
          title: 'Seleção de Empreendimentos',
          intro: 'Iniciamos o desenvolvimento de um anúncio com o empreendimento enviado. Vamos mostrar o que temos até então e gostaríamos de definir quais empreendimentos serão anunciados.'
        },
        {
          type: 'crm',
          title: 'Definir Necessidades Específicas de Conexão e CRM',
          question: 'Quais plataformas precisamos conectar e, após conectadas, o que vocês gostariam que fosse feito?'
        },
        {
          type: 'schedule',
          title: 'Agendar Reunião de Passagem de Acessos',
          day: 'Sexta-feira',
          times: ['14h', '16h']
        },
        {
          type: 'onboarding-summary',
          title: 'Ata do Onboarding',
          client: 'Paradise'
        }
      ]
    }
  };

  const slides = slidesData[clientId]?.[meetingId] || [];

  const handleClick = () => {
    const currentSlideData = slides[currentSlide];
    if (!currentSlideData) return;
    
    if (currentSlideData.type === 'section' || currentSlideData.type === 'needs' || currentSlideData.type === 'intro') {
      if (visibleItems.length < currentSlideData.items.length) {
        setVisibleItems([...visibleItems, currentSlideData.items[visibleItems.length]]);
      } else {
        setCurrentSlide(currentSlide + 1);
        setVisibleItems([]);
      }
    } else if (currentSlideData.type === 'comparison') {
      const maxItems = currentSlideData.ad3 ? 12 : 8;
      if (visibleItems.length < maxItems) {
        setVisibleItems([...visibleItems, visibleItems.length]);
      } else {
        setCurrentSlide(currentSlide + 1);
        setVisibleItems([]);
      }
    } else {
      if (currentSlide < slides.length - 1) {
        setCurrentSlide(currentSlide + 1);
        setVisibleItems([]);
      }
    }
  };

  const handleKeyPress = (e) => {
    // Ignorar se o foco estiver em campo de texto
    const tagName = e.target.tagName.toLowerCase();
    if (tagName === 'input' || tagName === 'textarea') {
      return;
    }
    
    if (e.key === 'ArrowRight' || e.key === ' ') {
      handleClick();
    } else if (e.key === 'ArrowLeft' && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
      setVisibleItems([]);
    }
  };

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentSlide, visibleItems]);

  if (!meeting) {
    return (
      <Container>
        <SlideTitle>Reunião não encontrada</SlideTitle>
      </Container>
    );
  }

  const renderSlide = () => {
    const slide = slides[currentSlide];
    if (!slide) return null;

    switch (slide.type) {
      case 'cover':
        return (
          <CoverSlide>
            <CoverTitle>{slide.title}</CoverTitle>
            <CoverSubtitle>{slide.subtitle}</CoverSubtitle>
            <ClickHint>Clique para continuar</ClickHint>
          </CoverSlide>
        );

      case 'divider':
        return (
          <CoverSlide>
            <CoverTitle>{slide.title}</CoverTitle>
            {slide.subtitle && <CoverSubtitle>{slide.subtitle}</CoverSubtitle>}
            <ClickHint>Clique para continuar</ClickHint>
          </CoverSlide>
        );

      case 'section':
        return (
          <SectionSlide>
            <SlideTitle>{slide.title}</SlideTitle>
            <TaskList>
              {visibleItems.map((item, index) => (
                <TaskItem key={index}>
                  <CheckIcon>✓</CheckIcon>
                  <TaskText>{item}</TaskText>
                </TaskItem>
              ))}
            </TaskList>
            {visibleItems.length < slide.items.length && (
              <ClickHint>Clique para próxima melhoria</ClickHint>
            )}
          </SectionSlide>
        );

      case 'needs':
        return (
          <NeedsSlide>
            <SlideTitle>{slide.title}</SlideTitle>
            <NeedsList>
              {visibleItems.map((item, index) => (
                <NeedItem key={index}>
                  <NeedIcon>{typeof item === 'object' ? item.icon : '📋'}</NeedIcon>
                  <NeedText>{typeof item === 'object' ? item.text : item}</NeedText>
                </NeedItem>
              ))}
            </NeedsList>
            {visibleItems.length < slide.items.length && (
              <ClickHint>Clique para continuar</ClickHint>
            )}
          </NeedsSlide>
        );

      case 'intro':
        return (
          <IntroSlide>
            <SlideTitle>{slide.title}</SlideTitle>
            <IntroContent>{slide.content}</IntroContent>
            <IntroList>
              {visibleItems.map((item, index) => (
                <IntroItem key={index}>
                  <BulletPoint>•</BulletPoint>
                  <IntroText>{item}</IntroText>
                </IntroItem>
              ))}
            </IntroList>
            {visibleItems.length < slide.items.length && (
              <ClickHint>Clique para próximo item</ClickHint>
            )}
          </IntroSlide>
        );

      case 'comparison':
        const maxItems = slide.ad3 ? 12 : 8;
        return (
          <ComparisonSlide>
            <SlideTitle>{slide.title}</SlideTitle>
            <ComparisonGrid hasThreeAds={!!slide.ad3}>
              <AdCard visible={visibleItems.length >= 1}>
                <AdTitle>{slide.ad1.name}</AdTitle>
                {slide.ad1.metrics.map((metric, index) => (
                  <MetricRow key={index} visible={visibleItems.length >= index + 2}>
                    <MetricLabel>{metric.label}</MetricLabel>
                    <MetricValue>{metric.value}</MetricValue>
                  </MetricRow>
                ))}
              </AdCard>
              <AdCard visible={visibleItems.length >= 4} highlight>
                <AdTitle winner>{slide.ad2.name}</AdTitle>
                {slide.ad2.metrics.map((metric, index) => (
                  <MetricRow key={index} visible={visibleItems.length >= index + 5}>
                    <MetricLabel>{metric.label}</MetricLabel>
                    <MetricValue>
                      {metric.value}
                      {metric.highlight && <Highlight>{metric.highlight}</Highlight>}
                    </MetricValue>
                  </MetricRow>
                ))}
                {visibleItems.length >= 8 && slide.ad2.note && <Note>{slide.ad2.note}</Note>}
              </AdCard>
              {slide.ad3 && (
                <AdCard visible={visibleItems.length >= 8}>
                  <AdTitle>{slide.ad3.name}</AdTitle>
                  {slide.ad3.metrics.map((metric, index) => (
                    <MetricRow key={index} visible={visibleItems.length >= index + 9}>
                      <MetricLabel>{metric.label}</MetricLabel>
                      <MetricValue>{metric.value}</MetricValue>
                    </MetricRow>
                  ))}
                </AdCard>
              )}
            </ComparisonGrid>
            {visibleItems.length < maxItems && (
              <ClickHint>Clique para revelar dados</ClickHint>
            )}
          </ComparisonSlide>
        );

      case 'analysis':
        return (
          <AnalysisSlide>
            <SlideTitle>{slide.title}</SlideTitle>
            <AnalysisList>
              {visibleItems.map((item, index) => (
                <AnalysisItem key={index} winner={item.winner}>
                  <AnalysisMetric>{item.metric}</AnalysisMetric>
                  <AnalysisComparison>{item.comparison}</AnalysisComparison>
                  <AnalysisWinner winner={item.winner}>{item.winner}</AnalysisWinner>
                </AnalysisItem>
              ))}
            </AnalysisList>
            {visibleItems.length >= slide.items.length && slide.conclusion && (
              <AnalysisConclusion>
                <ConclusionIcon>📊</ConclusionIcon>
                <ConclusionText>{slide.conclusion}</ConclusionText>
              </AnalysisConclusion>
            )}
            {visibleItems.length < slide.items.length && (
              <ClickHint>Clique para próxima análise</ClickHint>
            )}
          </AnalysisSlide>
        );

      case 'suggestion':
        return (
          <SuggestionSlide>
            <SlideTitle>{slide.title}</SlideTitle>
            <SuggestionBox>
              <SuggestionIcon>💡</SuggestionIcon>
              <SuggestionContent>{slide.content}</SuggestionContent>
              {slide.reason && (
                <SuggestionReason>
                  <ReasonLabel>Motivo:</ReasonLabel>
                  <ReasonText>{slide.reason}</ReasonText>
                </SuggestionReason>
              )}
            </SuggestionBox>
          </SuggestionSlide>
        );

      case 'summary':
        return (
          <SummarySlide>
            <SlideTitle>{slide.title}</SlideTitle>
            <SummaryDate>{slide.date}</SummaryDate>
            <SummarySections>
              {slide.sections.map((section, index) => (
                <SummarySection key={index}>
                  <SectionTitle>{section.title}</SectionTitle>
                  <SectionItems>
                    {section.items.map((item, itemIndex) => (
                      <SectionItem key={itemIndex}>
                        <ItemBullet>•</ItemBullet>
                        <ItemText>{item}</ItemText>
                      </SectionItem>
                    ))}
                  </SectionItems>
                </SummarySection>
              ))}
            </SummarySections>
            <AtaButton onClick={(e) => {
              e.stopPropagation();
              navigate(`/clientes/${clientId}/${meetingId}/ata`);
            }}>
              Ver Ata Completa →
            </AtaButton>
          </SummarySlide>
        );

      // Onboarding slide types for Lopes
      case 'onboarding-cover':
        return (
          <OnboardingCoverSlide>
            <OnboardingTitle>{slide.title}</OnboardingTitle>
            <ObjectivesSection>
              <ObjectivesLabel>Objetivos da reunião:</ObjectivesLabel>
              <ObjectivesList>
                {slide.objectives.map((obj, index) => (
                  <ObjectiveItem key={index}>
                    <ObjectiveNumber>{index + 1}</ObjectiveNumber>
                    <ObjectiveText>{obj}</ObjectiveText>
                  </ObjectiveItem>
                ))}
              </ObjectivesList>
            </ObjectivesSection>
            <ClickHint>Clique para continuar</ClickHint>
          </OnboardingCoverSlide>
        );

      case 'funnel':
        return (
          <FunnelSlide onClick={(e) => e.stopPropagation()}>
            <SlideTitle>{slide.title}</SlideTitle>
            <FunnelIntro>{slide.intro}</FunnelIntro>
            <FunnelContainer>
              <FunnelStage stage={1}>
                <FunnelLabel>Custo por Lead</FunnelLabel>
                <FunnelInputWrapper>
                  <FunnelPrefix>R$</FunnelPrefix>
                  <FunnelInput
                    type="text"
                    value={funnelData.custoLead}
                    onChange={(e) => setFunnelData({...funnelData, custoLead: e.target.value})}
                    placeholder="0,00"
                  />
                </FunnelInputWrapper>
              </FunnelStage>
              <FunnelArrow>↓</FunnelArrow>
              <FunnelStage stage={2}>
                <FunnelLabel>Custo por Agendamento</FunnelLabel>
                <FunnelInputWrapper>
                  <FunnelPrefix>R$</FunnelPrefix>
                  <FunnelInput
                    type="text"
                    value={funnelData.custoAgendamento}
                    onChange={(e) => setFunnelData({...funnelData, custoAgendamento: e.target.value})}
                    placeholder="0,00"
                  />
                </FunnelInputWrapper>
              </FunnelStage>
              <FunnelArrow>↓</FunnelArrow>
              <FunnelStage stage={3}>
                <FunnelLabel>Custo por Visita</FunnelLabel>
                <FunnelInputWrapper>
                  <FunnelPrefix>R$</FunnelPrefix>
                  <FunnelInput
                    type="text"
                    value={funnelData.custoVisita}
                    onChange={(e) => setFunnelData({...funnelData, custoVisita: e.target.value})}
                    placeholder="0,00"
                  />
                </FunnelInputWrapper>
              </FunnelStage>
              <FunnelArrow>↓</FunnelArrow>
              <FunnelStage stage={4}>
                <FunnelLabel>Custo por Venda</FunnelLabel>
                <FunnelInputWrapper>
                  <FunnelPrefix>R$</FunnelPrefix>
                  <FunnelInput
                    type="text"
                    value={funnelData.custoVenda}
                    onChange={(e) => setFunnelData({...funnelData, custoVenda: e.target.value})}
                    placeholder="0,00"
                  />
                </FunnelInputWrapper>
              </FunnelStage>
            </FunnelContainer>
            <NextSlideButton onClick={() => { setCurrentSlide(currentSlide + 1); setVisibleItems([]); }}>
              Próximo →
            </NextSlideButton>
          </FunnelSlide>
        );

      case 'paradise-funnel':
        const calculated = calculateParadiseFunnel();
        return (
          <FunnelSlide onClick={(e) => e.stopPropagation()}>
            <SlideTitle>{slide.title}</SlideTitle>
            <FunnelIntro>{slide.intro}</FunnelIntro>
            <FunnelContainer>
              {/* Verba */}
              <FunnelStage stage={1}>
                <FunnelLabel>Verba Total</FunnelLabel>
                <FunnelInputWrapper>
                  <FunnelPrefix>R$</FunnelPrefix>
                  <FunnelInput
                    type="text"
                    value={paradiseFunnelData.verba}
                    onChange={(e) => setParadiseFunnelData({...paradiseFunnelData, verba: e.target.value})}
                    placeholder="0,00"
                  />
                </FunnelInputWrapper>
              </FunnelStage>
              
              <FunnelArrow>↓</FunnelArrow>
              
              {/* Custo por Lead */}
              <FunnelStage stage={2}>
                <FunnelLabel>Custo por Lead</FunnelLabel>
                <FunnelInputWrapper>
                  <FunnelPrefix>R$</FunnelPrefix>
                  <FunnelInput
                    type="text"
                    value={paradiseFunnelData.custoLead}
                    onChange={(e) => setParadiseFunnelData({...paradiseFunnelData, custoLead: e.target.value})}
                    placeholder="0,00"
                  />
                </FunnelInputWrapper>
                <CalculatedValue>= {calculated.totalLeads} leads</CalculatedValue>
              </FunnelStage>
              
              <FunnelArrow>↓</FunnelArrow>
              
              {/* Taxa de Agendamento */}
              <FunnelStage stage={3}>
                <FunnelLabel>Taxa de Agendamento</FunnelLabel>
                <FunnelInputWrapper>
                  <FunnelInput
                    type="text"
                    value={paradiseFunnelData.taxaAgendamento}
                    onChange={(e) => setParadiseFunnelData({...paradiseFunnelData, taxaAgendamento: e.target.value})}
                    placeholder="0"
                    style={{ width: '80px' }}
                  />
                  <FunnelPrefix>%</FunnelPrefix>
                </FunnelInputWrapper>
                <CalculatedValue>= R$ {calculated.custoAgendamento}</CalculatedValue>
              </FunnelStage>
              
              <FunnelArrow>↓</FunnelArrow>
              
              {/* Taxa de Visita */}
              <FunnelStage stage={4}>
                <FunnelLabel>Taxa de Visita</FunnelLabel>
                <FunnelInputWrapper>
                  <FunnelInput
                    type="text"
                    value={paradiseFunnelData.taxaVisita}
                    onChange={(e) => setParadiseFunnelData({...paradiseFunnelData, taxaVisita: e.target.value})}
                    placeholder="0"
                    style={{ width: '80px' }}
                  />
                  <FunnelPrefix>%</FunnelPrefix>
                </FunnelInputWrapper>
                <CalculatedValue>= R$ {calculated.custoVisita}</CalculatedValue>
              </FunnelStage>
              
              <FunnelArrow>↓</FunnelArrow>
              
              {/* Taxa de Venda */}
              <FunnelStage stage={5}>
                <FunnelLabel>Taxa de Venda</FunnelLabel>
                <FunnelInputWrapper>
                  <FunnelInput
                    type="text"
                    value={paradiseFunnelData.taxaVenda}
                    onChange={(e) => setParadiseFunnelData({...paradiseFunnelData, taxaVenda: e.target.value})}
                    placeholder="0"
                    style={{ width: '80px' }}
                  />
                  <FunnelPrefix>%</FunnelPrefix>
                </FunnelInputWrapper>
                <CalculatedValue>= R$ {calculated.custoVenda}</CalculatedValue>
              </FunnelStage>
            </FunnelContainer>
            <NextSlideButton onClick={() => { setCurrentSlide(currentSlide + 1); setVisibleItems([]); }}>
              Próximo →
            </NextSlideButton>
          </FunnelSlide>
        );

      case 'empreendimentos':
        return (
          <EmpreendimentosSlide onClick={(e) => e.stopPropagation()}>
            <SlideTitle>{slide.title}</SlideTitle>
            <EmpreendimentosIntro>{slide.intro}</EmpreendimentosIntro>
            <NotesArea>
              <NotesTextarea
                value={notes.empreendimentos}
                onChange={(e) => setNotes({...notes, empreendimentos: e.target.value})}
                placeholder="Liste os empreendimentos selecionados aqui..."
              />
            </NotesArea>
            <NextSlideButton onClick={() => { setCurrentSlide(currentSlide + 1); setVisibleItems([]); }}>
              Próximo →
            </NextSlideButton>
          </EmpreendimentosSlide>
        );

      case 'crm':
        return (
          <CRMSlide onClick={(e) => e.stopPropagation()}>
            <SlideTitle>{slide.title}</SlideTitle>
            <CRMQuestion>{slide.question}</CRMQuestion>
            <NotesArea>
              <NotesTextarea
                value={notes.conexoesCRM}
                onChange={(e) => setNotes({...notes, conexoesCRM: e.target.value})}
                placeholder="Liste as plataformas e requisitos aqui..."
              />
            </NotesArea>
            <NextSlideButton onClick={() => { setCurrentSlide(currentSlide + 1); setVisibleItems([]); }}>
              Próximo →
            </NextSlideButton>
          </CRMSlide>
        );

      case 'schedule':
        return (
          <ScheduleSlide onClick={(e) => e.stopPropagation()}>
            <SlideTitle>{slide.title}</SlideTitle>
            <ScheduleDay>📅 {slide.day}</ScheduleDay>
            <ScheduleLabel>Sugestões de horário:</ScheduleLabel>
            <TimeButtonsContainer>
              {slide.times.map((time, index) => (
                <TimeButton
                  key={index}
                  selected={selectedTime === time}
                  onClick={() => setSelectedTime(time)}
                >
                  {time}
                  {selectedTime === time && <CheckMark>✓</CheckMark>}
                </TimeButton>
              ))}
            </TimeButtonsContainer>
            {selectedTime && (
              <ScheduleConfirmation>
                ✓ Horário selecionado: <strong>{selectedTime}</strong>
              </ScheduleConfirmation>
            )}
          </ScheduleSlide>
        );

      case 'onboarding-summary':
        const generateAtaText = () => {
          let text = `*📋 ATA DO ONBOARDING*\n`;
          text += `*${slide.client}*\n`;
          text += `${'='.repeat(40)}\n\n`;
          
          text += `*Metas do Funil de Aquisição:*\n`;
          
          // Use Paradise funnel data if client is Paradise
          if (clientId === 'paradise') {
            const calc = calculateParadiseFunnel();
            text += `• Verba Total: R$ ${paradiseFunnelData.verba}\n`;
            text += `• Custo por Lead: R$ ${paradiseFunnelData.custoLead}\n`;
            text += `• Total de Leads: ${calc.totalLeads}\n`;
            text += `• Taxa de Agendamento: ${paradiseFunnelData.taxaAgendamento}%\n`;
            text += `• Custo por Agendamento: R$ ${calc.custoAgendamento}\n`;
            text += `• Taxa de Visita: ${paradiseFunnelData.taxaVisita}%\n`;
            text += `• Custo por Visita: R$ ${calc.custoVisita}\n`;
            text += `• Taxa de Venda: ${paradiseFunnelData.taxaVenda}%\n`;
            text += `• Custo por Venda: R$ ${calc.custoVenda}\n\n`;
          } else {
            text += `• Custo por Lead: R$ ${funnelData.custoLead}\n`;
            text += `• Custo por Agendamento: R$ ${funnelData.custoAgendamento}\n`;
            text += `• Custo por Visita: R$ ${funnelData.custoVisita}\n`;
            text += `• Custo por Venda: R$ ${funnelData.custoVenda}\n\n`;
          }
          
          text += `*Empreendimentos Selecionados:*\n`;
          text += `${notes.empreendimentos}\n\n`;
          
          text += `*Necessidades de CRM:*\n`;
          text += `${notes.conexoesCRM}\n\n`;
          
          text += `*Próxima Reunião:*\n`;
          // Use different day for Paradise
          const meetingDay = clientId === 'paradise' ? 'Sexta-feira' : 'Segunda-feira';
          text += `• Dia: ${meetingDay}\n`;
          text += `• Horário: ${selectedTime}\n\n`;
          
          text += `${'='.repeat(40)}\n`;
          text += `_Gerado automaticamente pela BIANCA_`;
          
          return text;
        };
        
        const handleCopy = async () => {
          try {
            await navigator.clipboard.writeText(generateAtaText());
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } catch (err) {
            console.error('Erro ao copiar:', err);
          }
        };

        const calc = clientId === 'paradise' ? calculateParadiseFunnel() : null;
        const meetingDay = clientId === 'paradise' ? 'Sexta-feira' : 'Segunda-feira';
        
        return (
          <OnboardingSummarySlide onClick={(e) => e.stopPropagation()}>
            <SlideTitle>{slide.title}</SlideTitle>
            <SummaryContent>
              <SummaryBlock>
                <SummaryBlockTitle>📊 Metas do Funil</SummaryBlockTitle>
                <SummaryList>
                  {clientId === 'paradise' ? (
                    <>
                      <SummaryListItem>Verba Total: <strong>R$ {paradiseFunnelData.verba}</strong></SummaryListItem>
                      <SummaryListItem>Custo por Lead: <strong>R$ {paradiseFunnelData.custoLead}</strong></SummaryListItem>
                      <SummaryListItem>Total de Leads: <strong>{calc.totalLeads}</strong></SummaryListItem>
                      <SummaryListItem>Taxa de Agendamento: <strong>{paradiseFunnelData.taxaAgendamento}%</strong></SummaryListItem>
                      <SummaryListItem>Custo por Agendamento: <strong>R$ {calc.custoAgendamento}</strong></SummaryListItem>
                      <SummaryListItem>Taxa de Visita: <strong>{paradiseFunnelData.taxaVisita}%</strong></SummaryListItem>
                      <SummaryListItem>Custo por Visita: <strong>R$ {calc.custoVisita}</strong></SummaryListItem>
                      <SummaryListItem>Taxa de Venda: <strong>{paradiseFunnelData.taxaVenda}%</strong></SummaryListItem>
                      <SummaryListItem>Custo por Venda: <strong>R$ {calc.custoVenda}</strong></SummaryListItem>
                    </>
                  ) : (
                    <>
                      <SummaryListItem>Custo por Lead: <strong>R$ {funnelData.custoLead}</strong></SummaryListItem>
                      <SummaryListItem>Custo por Agendamento: <strong>R$ {funnelData.custoAgendamento}</strong></SummaryListItem>
                      <SummaryListItem>Custo por Visita: <strong>R$ {funnelData.custoVisita}</strong></SummaryListItem>
                      <SummaryListItem>Custo por Venda: <strong>R$ {funnelData.custoVenda}</strong></SummaryListItem>
                    </>
                  )}
                </SummaryList>
              </SummaryBlock>
              
              <SummaryBlock>
                <SummaryBlockTitle>🏢 Empreendimentos</SummaryBlockTitle>
                <SummaryText>{notes.empreendimentos}</SummaryText>
              </SummaryBlock>
              
              <SummaryBlock>
                <SummaryBlockTitle>🔗 Necessidades de CRM</SummaryBlockTitle>
                <SummaryText>{notes.conexoesCRM}</SummaryText>
              </SummaryBlock>
              
              <SummaryBlock>
                <SummaryBlockTitle>📅 Próxima Reunião</SummaryBlockTitle>
                <SummaryText>{meetingDay} às <strong>{selectedTime}</strong></SummaryText>
              </SummaryBlock>
            </SummaryContent>
            
            <CopyButton onClick={handleCopy} copied={copied}>
              {copied ? '✓ Copiado!' : '📋 Copiar Ata para WhatsApp'}
            </CopyButton>
          </OnboardingSummarySlide>
        );

      default:
        return null;
    }
  };

  return (
    <Container onClick={handleClick}>
      <ProgressBar>
        <Progress width={(currentSlide / (slides.length - 1)) * 100} />
      </ProgressBar>
      <SlideContainer>
        {renderSlide()}
      </SlideContainer>
      <SlideCounter>
        {currentSlide + 1} / {slides.length}
      </SlideCounter>
    </Container>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
`;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
  cursor: pointer;
  position: relative;
  overflow: hidden;
`;

const ProgressBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: rgba(100, 182, 255, 0.1);
  z-index: 1000;
`;

const Progress = styled.div`
  height: 100%;
  width: ${props => props.width}%;
  background: linear-gradient(90deg, #64B6FF 0%, #34CFEB 50%, #00FFFF 100%);
  transition: width 0.3s ease;
`;

const SlideContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
`;

const SlideCounter = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  font-family: 'Orbitron', sans-serif;
  color: rgba(100, 182, 255, 0.6);
  font-size: 0.9rem;
`;

const ClickHint = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  font-family: 'Orbitron', sans-serif;
  color: rgba(100, 182, 255, 0.5);
  font-size: 0.85rem;
  animation: ${pulse} 2s ease-in-out infinite;
`;

// Cover Slide
const CoverSlide = styled.div`
  text-align: center;
  max-width: 1200px;
  width: 100%;
`;

const CoverTitle = styled.h1`
  font-family: 'Orbitron', sans-serif;
  font-size: 4rem;
  font-weight: 600;
  background: linear-gradient(135deg, #64B6FF 0%, #34CFEB 50%, #00FFFF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 2rem;
  letter-spacing: 4px;
  animation: ${fadeIn} 0.8s ease;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const CoverSubtitle = styled.p`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.5rem;
  color: rgba(100, 182, 255, 0.8);
  letter-spacing: 2px;
  line-height: 1.6;
  animation: ${fadeIn} 0.8s ease 0.2s both;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

// Section Slide
const SectionSlide = styled.div`
  max-width: 1000px;
  width: 100%;
`;

const SlideTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: 3rem;
  font-weight: 600;
  background: linear-gradient(135deg, #64B6FF 0%, #34CFEB 50%, #00FFFF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 3rem;
  text-align: center;
  letter-spacing: 3px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const TaskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const TaskItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(100, 182, 255, 0.2);
  border-radius: 12px;
  padding: 1.5rem 2rem;
  animation: ${fadeIn} 0.5s ease;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(100, 182, 255, 0.05);
    border-color: rgba(100, 182, 255, 0.4);
    transform: translateX(10px);
  }
`;

const CheckIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #64B6FF 0%, #34CFEB 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0a0a0a;
  font-weight: bold;
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const TaskText = styled.p`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  flex: 1;
`;

// Needs Slide
const NeedsSlide = styled.div`
  max-width: 1000px;
  width: 100%;
`;

const NeedsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const NeedItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  background: rgba(255, 200, 100, 0.05);
  border: 2px solid rgba(255, 200, 100, 0.3);
  border-radius: 12px;
  padding: 2rem;
  animation: ${fadeIn} 0.5s ease;
`;

const NeedIcon = styled.div`
  font-size: 2rem;
  flex-shrink: 0;
`;

const NeedText = styled.p`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.2rem;
  color: rgba(255, 200, 100, 0.9);
  line-height: 1.6;
  flex: 1;
`;

// Intro Slide
const IntroSlide = styled.div`
  max-width: 1000px;
  width: 100%;
`;

const IntroContent = styled.p`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 3rem;
  line-height: 1.8;
`;

const IntroList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const IntroItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  animation: ${fadeIn} 0.5s ease;
`;

const BulletPoint = styled.div`
  font-size: 2rem;
  color: #64B6FF;
  line-height: 1;
  margin-top: 0.2rem;
`;

const IntroText = styled.p`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  flex: 1;
`;

// Comparison Slide
const ComparisonSlide = styled.div`
  max-width: 1400px;
  width: 100%;
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.hasThreeAds ? '1fr 1fr 1fr' : '1fr 1fr'};
  gap: ${props => props.hasThreeAds ? '2rem' : '3rem'};
  margin-bottom: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: ${props => props.hasThreeAds ? '1fr 1fr' : '1fr 1fr'};
  }

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const AdCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 2px solid ${props => props.highlight ? 'rgba(100, 255, 100, 0.4)' : 'rgba(100, 182, 255, 0.3)'};
  border-radius: 16px;
  padding: 2.5rem;
  opacity: ${props => props.visible ? 1 : 0};
  transform: translateY(${props => props.visible ? 0 : '20px'});
  transition: all 0.5s ease;
  ${props => props.highlight && `
    box-shadow: 0 0 30px rgba(100, 255, 100, 0.2);
  `}
`;

const AdTitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.8rem;
  color: ${props => props.winner ? '#64FF64' : '#64B6FF'};
  margin-bottom: 2rem;
  text-align: center;
  letter-spacing: 2px;
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem 0;
  border-bottom: 1px solid rgba(100, 182, 255, 0.1);
  opacity: ${props => props.visible ? 1 : 0};
  transform: translateX(${props => props.visible ? 0 : '-20px'});
  transition: all 0.4s ease;

  &:last-child {
    border-bottom: none;
  }
`;

const MetricLabel = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
`;

const MetricValue = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.3rem;
  font-weight: 600;
  color: #fff;
  text-align: right;
`;

const Highlight = styled.div`
  font-size: 0.9rem;
  color: #64FF64;
  margin-top: 0.5rem;
  font-weight: 400;
`;

const Note = styled.p`
  font-family: 'Orbitron', sans-serif;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 1.5rem;
  font-style: italic;
  line-height: 1.5;
  animation: ${fadeIn} 0.5s ease;
`;

// Analysis Slide
const AnalysisSlide = styled.div`
  max-width: 1000px;
  width: 100%;
`;

const AnalysisList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 2rem;
`;

const AnalysisItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid ${props => props.winner === 'AD 2' ? 'rgba(100, 182, 255, 0.5)' : 'rgba(255, 200, 100, 0.5)'};
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const AnalysisMetric = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.2rem;
  color: #fff;
  margin: 0;
`;

const AnalysisComparison = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.5;
`;

const AnalysisWinner = styled.span`
  font-family: 'Orbitron', sans-serif;
  font-size: 0.9rem;
  color: ${props => props.winner === 'AD 2' ? '#64B6FF' : '#FFD700'};
  background: ${props => props.winner === 'AD 2' ? 'rgba(100, 182, 255, 0.1)' : 'rgba(255, 215, 0, 0.1)'};
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  width: fit-content;
  margin-top: 0.5rem;
`;

const AnalysisConclusion = styled.div`
  background: linear-gradient(135deg, rgba(100, 182, 255, 0.1) 0%, rgba(100, 182, 255, 0.05) 100%);
  border: 1px solid rgba(100, 182, 255, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 2rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const ConclusionIcon = styled.span`
  font-size: 2rem;
`;

const ConclusionText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  line-height: 1.6;
`;

// Suggestion Slide
const SuggestionSlide = styled.div`
  max-width: 1000px;
  width: 100%;
`;

const SuggestionBox = styled.div`
  background: rgba(100, 255, 100, 0.05);
  border: 2px solid rgba(100, 255, 100, 0.3);
  border-radius: 16px;
  padding: 3rem;
  text-align: center;
`;

const SuggestionIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 2rem;
`;

const SuggestionContent = styled.p`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.8rem;
  color: #64FF64;
  font-weight: 600;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const SuggestionReason = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 2rem;
  margin-top: 2rem;
`;

const ReasonLabel = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const ReasonText = styled.p`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
`;

// Summary Slide
const SummarySlide = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const SummaryDate = styled.p`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.2rem;
  color: rgba(100, 182, 255, 0.7);
  text-align: center;
  margin-bottom: 3rem;
`;

const SummarySections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
`;

const SummarySection = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(100, 182, 255, 0.2);
  border-radius: 12px;
  padding: 2rem;
`;

const SectionTitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.5rem;
  color: #64B6FF;
  margin-bottom: 1.5rem;
  letter-spacing: 2px;
`;

const SectionItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const ItemBullet = styled.div`
  color: #64B6FF;
  font-size: 1.5rem;
  line-height: 1;
  margin-top: 0.2rem;
`;

const ItemText = styled.p`
  font-family: 'Orbitron', sans-serif;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  flex: 1;
`;

const AtaButton = styled.button`
  margin-top: 3rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #64B6FF 0%, #34CFEB 100%);
  border: none;
  border-radius: 12px;
  font-family: 'Orbitron', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  color: #0a0a0a;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 1px;
  display: block;
  margin-left: auto;
  margin-right: auto;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(100, 182, 255, 0.4);
  }

  &:active {
    transform: translateY(-2px);
  }
`;

// Onboarding Slides Styles
const OnboardingCoverSlide = styled.div`
  text-align: center;
  max-width: 1000px;
  width: 100%;
`;

const OnboardingTitle = styled.h1`
  font-family: 'Orbitron', sans-serif;
  font-size: 3.5rem;
  font-weight: 600;
  background: linear-gradient(135deg, #64B6FF 0%, #34CFEB 50%, #00FFFF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 3rem;
  letter-spacing: 4px;
  animation: ${fadeIn} 0.8s ease;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ObjectivesSection = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(100, 182, 255, 0.3);
  border-radius: 16px;
  padding: 2.5rem;
  text-align: left;
  animation: ${fadeIn} 0.8s ease 0.3s both;
`;

const ObjectivesLabel = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.3rem;
  color: rgba(100, 182, 255, 0.9);
  margin-bottom: 2rem;
  letter-spacing: 2px;
`;

const ObjectivesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const ObjectiveItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const ObjectiveNumber = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #64B6FF 0%, #34CFEB 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Orbitron', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: #0a0a0a;
  flex-shrink: 0;
`;

const ObjectiveText = styled.p`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
`;

// Funnel Slide
const FunnelSlide = styled.div`
  max-width: 800px;
  width: 100%;
  cursor: default;
`;

const FunnelIntro = styled.p`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 3rem;
  line-height: 1.8;
`;

const FunnelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const FunnelStage = styled.div`
  width: ${props => 100 - (props.stage - 1) * 15}%;
  min-width: 280px;
  max-width: 100%;
  background: rgba(100, 182, 255, ${props => 0.05 + props.stage * 0.03});
  border: 2px solid rgba(100, 182, 255, ${props => 0.2 + props.stage * 0.1});
  border-radius: 12px;
  padding: 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(100, 182, 255, 0.6);
    background: rgba(100, 182, 255, ${props => 0.08 + props.stage * 0.03});
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FunnelLabel = styled.span`
  font-family: 'Orbitron', sans-serif;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 1px;
  text-align: center;
  width: 100%;
`;

const FunnelInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FunnelPrefix = styled.span`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.1rem;
  color: #64B6FF;
  font-weight: 600;
`;

const FunnelInput = styled.input`
  width: 120px;
  padding: 0.75rem 1rem;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(100, 182, 255, 0.4);
  border-radius: 8px;
  font-family: 'Orbitron', sans-serif;
  font-size: 1.1rem;
  color: #fff;
  text-align: right;
  outline: none;
  transition: all 0.3s ease;

  &:focus {
    border-color: #64B6FF;
    box-shadow: 0 0 15px rgba(100, 182, 255, 0.3);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const FunnelArrow = styled.div`
  font-size: 1.5rem;
  color: rgba(100, 182, 255, 0.5);
`;

const CalculatedValue = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 0.95rem;
  color: #64FF64;
  text-align: center;
  font-weight: 600;
  letter-spacing: 1px;
  width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const NextSlideButton = styled.button`
  margin-top: 3rem;
  padding: 1rem 2.5rem;
  background: linear-gradient(135deg, #64B6FF 0%, #34CFEB 100%);
  border: none;
  border-radius: 12px;
  font-family: 'Orbitron', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  color: #0a0a0a;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 1px;
  display: block;
  margin-left: auto;
  margin-right: auto;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(100, 182, 255, 0.4);
  }

  &:active {
    transform: translateY(-2px);
  }
`;

// Empreendimentos Slide
const EmpreendimentosSlide = styled.div`
  max-width: 900px;
  width: 100%;
  cursor: default;
`;

const EmpreendimentosIntro = styled.p`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 2.5rem;
  line-height: 1.8;
`;

const NotesArea = styled.div`
  width: 100%;
`;

const NotesTextarea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.4);
  border: 2px solid rgba(100, 182, 255, 0.3);
  border-radius: 12px;
  font-family: 'Orbitron', sans-serif;
  font-size: 1rem;
  color: #fff;
  line-height: 1.8;
  resize: vertical;
  outline: none;
  transition: all 0.3s ease;

  &:focus {
    border-color: #64B6FF;
    box-shadow: 0 0 20px rgba(100, 182, 255, 0.2);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

// CRM Slide
const CRMSlide = styled.div`
  max-width: 900px;
  width: 100%;
  cursor: default;
`;

const CRMQuestion = styled.p`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.3rem;
  color: rgba(100, 182, 255, 0.9);
  text-align: center;
  margin-bottom: 2.5rem;
  line-height: 1.8;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(100, 182, 255, 0.2);
  border-radius: 12px;
  padding: 1.5rem 2rem;
`;

// Schedule Slide
const ScheduleSlide = styled.div`
  max-width: 700px;
  width: 100%;
  text-align: center;
  cursor: default;
`;

const ScheduleDay = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 2rem;
  color: #64B6FF;
  margin-bottom: 2rem;
  letter-spacing: 2px;
`;

const ScheduleLabel = styled.p`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 2rem;
`;

const TimeButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
`;

const TimeButton = styled.button`
  padding: 1.5rem 3rem;
  background: ${props => props.selected 
    ? 'linear-gradient(135deg, #64B6FF 0%, #34CFEB 100%)' 
    : 'rgba(255, 255, 255, 0.05)'};
  border: 2px solid ${props => props.selected 
    ? '#64B6FF' 
    : 'rgba(100, 182, 255, 0.3)'};
  border-radius: 12px;
  font-family: 'Orbitron', sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.selected ? '#0a0a0a' : '#fff'};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-width: 140px;

  &:hover {
    transform: translateY(-4px);
    border-color: #64B6FF;
    box-shadow: 0 8px 24px rgba(100, 182, 255, 0.3);
  }
`;

const CheckMark = styled.span`
  position: absolute;
  top: -10px;
  right: -10px;
  width: 28px;
  height: 28px;
  background: #64FF64;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: #0a0a0a;
`;

const ScheduleConfirmation = styled.div`
  margin-top: 3rem;
  padding: 1.5rem 2rem;
  background: rgba(100, 255, 100, 0.1);
  border: 1px solid rgba(100, 255, 100, 0.3);
  border-radius: 12px;
  font-family: 'Orbitron', sans-serif;
  font-size: 1.2rem;
  color: #64FF64;
  animation: ${fadeIn} 0.5s ease;

  strong {
    color: #fff;
  }
`;

// Onboarding Summary Slide
const OnboardingSummarySlide = styled.div`
  max-width: 1000px;
  width: 100%;
  cursor: default;
`;

const SummaryContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryBlock = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(100, 182, 255, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
`;

const SummaryBlockTitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.1rem;
  color: #64B6FF;
  margin-bottom: 1rem;
  letter-spacing: 1px;
`;

const SummaryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SummaryListItem = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.8);

  strong {
    color: #fff;
  }
`;

const SummaryText = styled.p`
  font-family: 'Orbitron', sans-serif;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  white-space: pre-line;

  strong {
    color: #64B6FF;
  }
`;

const CopyButton = styled.button`
  padding: 1.2rem 3rem;
  background: ${props => props.copied 
    ? 'linear-gradient(135deg, #64FF64 0%, #32CD32 100%)' 
    : 'linear-gradient(135deg, #64B6FF 0%, #34CFEB 100%)'};
  border: none;
  border-radius: 12px;
  font-family: 'Orbitron', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  color: #0a0a0a;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 1px;
  display: block;
  margin: 2rem auto 0;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px ${props => props.copied 
      ? 'rgba(100, 255, 100, 0.4)' 
      : 'rgba(100, 182, 255, 0.4)'};
  }

  &:active {
    transform: translateY(-2px);
  }
`;

export default MeetingPresentation;
